import { FC, useEffect, useState } from "react"
import GlobalSpinner from "../globalsSpinner/GlobalSpinner";
import Select, { MultiValue } from "react-select";
import { Guid } from "../../../api/baseApi";
import { getPlayersShort } from "../../../api/playersApi";
import { daysSinceLastMonday, daysSinceStartOfMonth, formatDate, formatMsToHHMMSS, generateUniqueId, getDaysInPreviousMonth, getPastDateByDaysAgo, isFirstDateGreaterThanSecond } from "../../../services/dateService";
import Pagination from "../pagination/Pagination";
import { Button } from "../Button";
import "./ReportDashboard.css"
import { FormInput, FormRow } from "../formRow/FormRow";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { downloadCSVReportsDashboardData, getReportsDashboardData, PlayoutsPerDate, PlayoutsReportRow, postReportsDashboardDataRequest, ReportDashboardColumns } from "../../../api/reportsApi";
import { ReactSortable } from "react-sortablejs";
import { getCampaignsBase } from "../../../api/campaingsApi";
import { getChannelsShort } from "../../../api/channelsApi";
import { getMediaBaseInfo, MediaContentType, mediaContentTypeOptions } from "../../../api/mediaApi";
import { getPagesCount } from "../../../services/paginator";
import { RightModal, SmallModal } from "../sideModal/Modal";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Routes } from "../../../constants/routes";
import ReportSubscriptions from "../reportSubscriptions/ReportSubscriptions";
import fileDownload from "js-file-download";
import IconButton from "../iconButton/IconButton";
import { formatDuration } from "../../../services/common";

type Option<T> = { value: T, label: string}
type Slice = {
    id: number,
    name: string,
    isActive: boolean
}

type SelectedDatePeriodType = "" | "Today" | "Yesterday" | "WeekToDate" | "LastWeek" | "Last7Days" | "LastMonth" | "MonthToDate"

type Preset = {
    id: string,
    name: string,
    startDate: string,
    endDate: string,
    periodType: SelectedDatePeriodType,
    slices: Slice[],
    campaignIds: Guid[],
    playerIds: Guid[],
    networkIds: Guid[],
    mediaIds: Guid[],
    contentTypes: number[]
}

const startSliceData:Slice[] = [{
    id: ReportDashboardColumns.Campaign,
    name: ReportDashboardColumns[ReportDashboardColumns.Campaign],
    isActive: true
},
{
    id: ReportDashboardColumns.Network,
    name: ReportDashboardColumns[ReportDashboardColumns.Network],
    isActive: false
},
{
    id: ReportDashboardColumns.Player,
    name: ReportDashboardColumns[ReportDashboardColumns.Player],
    isActive: false
},
{
    id: ReportDashboardColumns.Media,
    name: ReportDashboardColumns[ReportDashboardColumns.Media],
    isActive: false
},
{
    id: ReportDashboardColumns.ContentType,
    name: ReportDashboardColumns[ReportDashboardColumns.ContentType],
    isActive: false
},
]

type DurationMode = "Hours" | "Minutes"

const ReportDashboard:FC<RouteComponentProps> = (props) => {
    let [isLoading, setIsLoading] = useState(false);

    let [filteredPlayers, setFilteredPlayers ] = useState<Option<Guid>[]>([]);
    let [filteredCampaigns, setFilteredCampaigns ] = useState<Option<Guid>[]>([]);
    let [filteredNetworks, setFilteredNetworks ] = useState<Option<Guid>[]>([]);
    let [filteredMedias, setFilteredMedias ] = useState<Option<Guid>[]>([]);
    let [filteredContentTypes, setFilteredContentTypes ] = useState<Option<number>[]>([]);
    let [selectedPlayers, setSelectedPlayers] = useState<Guid[]>([]);
    let [selectedCampaigns, setSelectedCampaigns] = useState<Guid[]>([]);
    let [selectedNetworks, setSelectedNetworks] = useState<Guid[]>([]);
    let [selectedMedias, setSelectedMedias] = useState<Guid[]>([]);
    let [selectedContentTypes, setSelectedContentTypes] = useState<number[]>([]);


    let [currentPage, setCurrentPage] = useState<number>(1);

    let [selectedStartDate, setSelectedStartDate] = useState<string>("")
    let [isSelectedStartDateError, setIsSelectedStartDateError] = useState(false)
    let [selectedEndDate, setSelectedEndDate] = useState<string>(getPastDateByDaysAgo(0))
    let [isSelectedEndDateError, setIsSelectedEndDateError] = useState(false)

    let [playoutsPerDay, setPlayoutsPerDay] = useState<PlayoutsPerDate[]>([])

    let [reportPlayouts, setReportPlayouts] = useState<PlayoutsReportRow[]>([])
    let [reportPlayoutsForDisplay, setReportPlayoutsForDisplay] = useState<PlayoutsReportRow[]>([])

    let [selectedDatePeriodType, setSelectedDatePeriodType] = useState<SelectedDatePeriodType>("LastMonth")

    let [sliceColumns, setSliceColumns] = useState<number[]>([])


    let [isShowSmallModal, setIsShowSmallModal] = useState(false)
    let [isShowPresetModal, setIsShowPresetModal] = useState(false)
    let [isShowSheduledReportsModal, setIsShowSheduledReportsModal] = useState(false)
    let [isShowConfirmResetModal, setIsShowConfirmResetModal] = useState(false)

    let [presetName, setPresetName] = useState("");
    let [isPresetNameError, setIsPresetNameError] = useState(false);
    let [presetNameErrorMessage, setPresetNameErrorMessage] = useState("");
    let [presets, setPresets] = useState<Preset[]>([])

    let [slices, setSlices] = useState<Slice[]>(startSliceData)

    let [totalPlayouts, setTotalPlayouts] = useState(0)
    let [totalDurationString, setTotalDurationString] = useState("")
    let [totalDurationSeconds, setTotalDurationSeconds] = useState(0)
    let [durationMode, setDurationMode] = useState<DurationMode>("Hours")

    let itemsPerPage = 20;

    useEffect(()=>{
        async function fetchProfiles(){
            setIsLoading(true);
            setMonthToDatePeriod();
            setPresets(getPresetsState())
            setFilteredContentTypes(getContentTypeOptions())
            await Promise.all([updatePlayers(), updateCampaigns(), updateNetworks(), updateMedias()]);
            setIsLoading(false);
        }

        fetchProfiles()
    },[])

    const getContentTypeOptions = () => mediaContentTypeOptions.map( el => ({label: el.name, value: el.type}))

    const renderContentTypeName = (type: MediaContentType) => {
        switch(type) {
            case MediaContentType.Ads : return "Campaigns"
            case MediaContentType.ClientContent : return "Network Content"
            case MediaContentType.SelfContent: return "Inhouse Content"
            case MediaContentType.Backup: return "Others"
        }
    }

    useEffect(() => {
        let startIndex = (currentPage - 1)*itemsPerPage;
        let finishIndex = startIndex + itemsPerPage;
        setReportPlayoutsForDisplay(reportPlayouts.slice(startIndex, finishIndex));
    },[reportPlayouts,currentPage])

    useEffect(() => {
        validateReportPeriodDates(selectedStartDate,selectedEndDate)
    }, [selectedEndDate,selectedStartDate])

    const updateCampaigns = async () => {
        var result = await getCampaignsBase();
        if(result.type === "succes") {
            let t = result.data.items.map(el => {
                return {
                    label: el.name,
                    value: el.id
                }
            })
            setFilteredCampaigns(t)
        }
    }
    const updateNetworks = async () => {
        var result = await getChannelsShort();
        if(result.type === "succes") {
            let t = result.data.items.map(el => {
                return {
                    label: el.name,
                    value: el.id
                }
            })
            setFilteredNetworks(t)
        }
    }
    const updateMedias = async () => {
        var result = await getMediaBaseInfo();
        if(result.type === "succes") {
            let t = result.data.items.map(el => {
                return {
                    label: el.name,
                    value: el.id
                }
            })
            setFilteredMedias(t)
        }
    }

    const updatePlayers = async () => {
        var result = await getPlayersShort();
        if(result.type === "succes"){
            let t = result.data.items.map( el => {
                return {
                    label: el.name,
                    value: el.id
                }
            })
            setFilteredPlayers(t);
        }
    }

    const updateReportDashboardData = async () => {

        let isValid = true

        if(selectedStartDate === ""){
            setIsSelectedStartDateError(true)
            isValid = false
        }
        if(selectedEndDate === ""){
            setIsSelectedEndDateError(true)
            isValid = false
        }

        if(!isValid) return

        setCurrentPage(1)
        let result = await getReportsDashboardData({
            startTime: selectedStartDate,
            endTime: selectedEndDate,
            columns: getColumnsForRequest(slices),
            networkIds: selectedNetworks,
            campaignIds: selectedCampaigns,
            playerIds: selectedPlayers,
            mediaIds: selectedMedias,
            contentTypes: selectedContentTypes
        })
        if(result.type === "succes") {
            let durMode = checkMode(result.data.data.totalDurationSeconds, result.data.data.chart.length)
            setReportPlayouts(result.data.data.tableRows)
            setSliceColumns(result.data.data.columns)
            setTotalPlayouts(result.data.data.totalPlayouts)
            setTotalDurationString(result.data.data.totalDurationString)
            setTotalDurationSeconds(result.data.data.totalDurationSeconds)
            setDurationMode(durMode)
            setPlayoutsPerDay(result.data.data.chart.map(t => {
                return {
                    date: formatDate(t.date),
                    playouts: t.playouts,
                    durationSeconds: durationFormatter(durMode,t.durationSeconds)
                }
            }))
        }
    }

    const resetReportDashboardData = () => {
        setSlices(startSliceData)
        setSliceColumns([])
        setPlayoutsPerDay([])
        setReportPlayouts([])
        setSelectedDatePeriodType("")
        setSelectedStartDate("")
        setIsSelectedStartDateError(false)
        setSelectedEndDate("")
        setIsSelectedEndDateError(false)
        setSelectedCampaigns([])
        setSelectedNetworks([])
        setSelectedPlayers([])
        setSelectedMedias([])
        setSelectedContentTypes([])
        setCurrentPage(1)
        setIsShowConfirmResetModal(false)
    }

    const updateReportDashboardDataForAdhoc = async () => {
        let result = await postReportsDashboardDataRequest({
            startTime: selectedStartDate,
            endTime: selectedEndDate,
            columns: getColumnsForRequest(slices),
            networkIds: selectedNetworks,
            campaignIds: selectedCampaigns,
            playerIds: selectedPlayers,
            mediaIds: selectedMedias,
            contentTypes: selectedContentTypes
        })
        if(result.type === "succes") {
            setIsShowSmallModal(true)
        }
    }

    const updateReportDashboardDataFromPreset = async (preset: Preset) => {
        setIsLoading(true)
        setCurrentPage(1)
        let result = await getReportsDashboardData({
            startTime: preset.startDate,
            endTime: preset.endDate,
            columns: getColumnsForRequest(preset.slices),
            networkIds: preset.networkIds,
            campaignIds: preset.campaignIds,
            playerIds: preset.playerIds,
            mediaIds: preset.mediaIds,
            contentTypes: preset.contentTypes
        })
        if(result.type === "succes") {
            let durMode = checkMode(result.data.data.totalDurationSeconds, result.data.data.chart.length)
            setReportPlayouts(result.data.data.tableRows)
            setSliceColumns(result.data.data.columns)
            setTotalPlayouts(result.data.data.totalPlayouts)
            setTotalDurationString(result.data.data.totalDurationString)
            setTotalDurationSeconds(result.data.data.totalDurationSeconds)
            setDurationMode(durMode)
            setPlayoutsPerDay(result.data.data.chart.map(t => {
                return {
                    date: formatDate(t.date),
                    playouts: t.playouts,
                    durationSeconds: durationFormatter(durMode,t.durationSeconds)
                }
            }))
        }
        setIsLoading(false)
    }

    const checkMode = (a:number, b:number): DurationMode => a/b >= 3600 ? "Hours" : "Minutes"

    const durationFormatter = (durationMode: DurationMode,durationSeconds: number) => {
        let hours = Math.floor(durationSeconds*100 / 3600)/100;
        let minutes = Math.floor(durationSeconds*100 / 60)/100;
        return durationMode == "Hours" ? hours : minutes
    }

    const onStartDateChanged = (date: string) => {
        setSelectedStartDate(date);
        setSelectedDatePeriodType("")
    }
    const onEndDateChanged = (date: string) => {
        setSelectedEndDate(date);
        setSelectedDatePeriodType("")
    }

    const validateReportPeriodDates = (start: string, end: string) => {
        if(isFirstDateGreaterThanSecond(start, end)){
            setIsSelectedEndDateError(true)
            setIsSelectedStartDateError(true)
        } else {
            setIsSelectedEndDateError(false)
            setIsSelectedStartDateError(false)
        }
    }

    const setTodayPeriod = () => {
        let start = getPastDateByDaysAgo(0)
        let end = getPastDateByDaysAgo(0)
        setSelectedStartDate(start);
        setSelectedEndDate(end)
        setSelectedDatePeriodType("Today")
        return {start, end}
    }

    const setYesterdayPeriod = () => {
        let start = getPastDateByDaysAgo(1)
        let end = getPastDateByDaysAgo(1)
        setSelectedStartDate(start);
        setSelectedEndDate(end)
        setSelectedDatePeriodType("Yesterday")
        return {start, end}
    }

    const setWeekToDatePeriod = () => {
        let start = getPastDateByDaysAgo(daysSinceLastMonday())
        let end = getPastDateByDaysAgo(0)
        setSelectedStartDate(start);
        setSelectedEndDate(end)
        setSelectedDatePeriodType("WeekToDate")
        return {start, end}
    }

    const setLastWeekPeriod = () => {
        let start = getPastDateByDaysAgo(daysSinceLastMonday() + 7)
        let end = getPastDateByDaysAgo(daysSinceLastMonday() + 1)
        setSelectedStartDate(start);
        setSelectedEndDate(end)
        setSelectedDatePeriodType("LastWeek")
        return {start, end}
    }

    const setMonthToDatePeriod = () => {
        let start = getPastDateByDaysAgo(daysSinceStartOfMonth())
        let end = getPastDateByDaysAgo(0)
        setSelectedStartDate(start);
        setSelectedEndDate(end)
        setSelectedDatePeriodType("MonthToDate")
        return {start, end}
    }

    const setLast7DaysPeriod = () => {
        let start = getPastDateByDaysAgo(7)
        let end = getPastDateByDaysAgo(1)
        setSelectedStartDate(start);
        setSelectedEndDate(end)
        setSelectedDatePeriodType("Last7Days")
        return {start, end}
    }

    const setLastMonthPeriod = () => {
        let start = getPastDateByDaysAgo(daysSinceStartOfMonth() + getDaysInPreviousMonth())
        let end = getPastDateByDaysAgo(daysSinceStartOfMonth() + 1)
        setSelectedStartDate(start);
        setSelectedEndDate(end)
        setSelectedDatePeriodType("LastMonth")
        return {start, end}
    }


    const onChangeSelectedPlayer = (newValue: MultiValue<Option<Guid>>) => {
        if(newValue){
            let newIds = newValue.map(el => el.value)
            setSelectedPlayers(newIds);
        } else {
            setSelectedPlayers([]);
        }
    }
    const onChangeSelectedCampaign = (newValue: MultiValue<Option<Guid>>) => {
        if(newValue){
            let newIds = newValue.map(el => el.value)
            setSelectedCampaigns(newIds);
        } else {
            setSelectedCampaigns([]);
        }
    }
    const onChangeSelectedNetwork = (newValue: MultiValue<Option<Guid>>) => {
        if(newValue){
            let newIds = newValue.map(el => el.value)
            setSelectedNetworks(newIds);
        } else {
            setSelectedNetworks([]);
        }
    }
    const onChangeSelectedMedia = (newValue: MultiValue<Option<Guid>>) => {
        if(newValue){
            let newIds = newValue.map(el => el.value)
            setSelectedMedias(newIds);
        } else {
            setSelectedMedias([]);
        }
    }
    const onChangeSelectedContentType = (newValue: MultiValue<Option<number>>) => {
        if(newValue){
            let newIds = newValue.map(el => el.value)
            setSelectedContentTypes(newIds);
        } else {
            setSelectedContentTypes([]);
        }
    }

    const renderPlayoutRows = (rows: PlayoutsReportRow[], columns: ReportDashboardColumns[]) => {
        return rows.map( (item,i) => {
            return <tr key={i}>
                {columns.map((el,j) => {
                    return <td key={j}>{getCellValue(item, el)}</td>
                })}
                <td>{item.playouts}</td>
                <td>{formatMsToHHMMSS(item.duration)}</td>
            </tr>
        })
    }

    const renderTableTitles = (columns: ReportDashboardColumns[]) => {
        return columns.map((el,i) => {
            return <td key={i}>{ReportDashboardColumns[el]}</td>
        })
    }

    const changeSliceStatus = (id: number) => {
        setSlices(prevSlices => 
            prevSlices.map((slice) =>
              slice.id === id ? { ...slice, isActive: !slice.isActive } : slice
            )
        );
    }

    const getColumnsForRequest = (slices: Slice[]) => {
        return slices.filter(t => t.isActive).map(t => t.id)
    }

    const renderSlices = (slices: Slice[]) => {
        return slices.map( (it) => {
            return <span className="slices__elem" key={it.id}>{it.name} &nbsp;
                <label className="checkbox-control">
                    <input 
                        id={`slice-${it.name}-${it.id}`} 
                        className="checkbox" 
                        type="checkbox" 
                        checked={it.isActive}
                        onChange={() => {changeSliceStatus(it.id)}}
                    />
                    <span className="checkbox-bg"></span>
                    <span className="checkbox-cursor"></span>
                </label>
            </span>
        })
    }

    const getCellValue = (rowTable: PlayoutsReportRow, currentColumn: number) => {
        switch(currentColumn) {
            case ReportDashboardColumns.Network : return rowTable.network
            case ReportDashboardColumns.Campaign : return rowTable.campaign
            case ReportDashboardColumns.Player: return rowTable.player
            case ReportDashboardColumns.Media: return rowTable.media
            case ReportDashboardColumns.ContentType: return renderContentTypeName(rowTable.contentType)
        }
    }

    const checkActiveDatePeriodType = (type: SelectedDatePeriodType) => {
        return selectedDatePeriodType === type ? 'active' : null
    }

    const hidePresetModal = () => {
        setPresetName("")
        setIsPresetNameError(false)
        setPresetNameErrorMessage("")
        setIsShowPresetModal(false)
    }

    const createNewPreset = async () => {
        if(presetName === "") {
            setIsPresetNameError(true);
            setPresetNameErrorMessage("Please enter Preset Name")
            return;
        }
        
        let presetIndex = presets.findIndex(p => p.name === presetName)

        if(presetIndex !== -1){
            setIsPresetNameError(true);
            setPresetNameErrorMessage("This Preset is already exists")
            return;
        }
        
        let newPreset: Preset 

        if(selectedDatePeriodType !== "") {
            newPreset = {
                id: generateUniqueId(),
                name: presetName,
                slices: slices,
                periodType: selectedDatePeriodType,
                startDate: "",
                endDate: "",
                campaignIds: selectedCampaigns,
                networkIds: selectedNetworks,
                playerIds: selectedPlayers,
                mediaIds: selectedMedias,
                contentTypes: selectedContentTypes
            };
        } else {
            newPreset = {
                id: generateUniqueId(),
                name: presetName,
                slices: slices,
                periodType: "",
                startDate: selectedStartDate,
                endDate: selectedEndDate,
                campaignIds: selectedCampaigns,
                networkIds: selectedNetworks,
                playerIds: selectedPlayers,
                mediaIds: selectedMedias,
                contentTypes: selectedContentTypes
            };
        }

        let newPresetState = [...presets, newPreset]
        saveCurrentPresetState(newPresetState)
        
        hidePresetModal()
    }

    const getPresetsState = () => {
        const presetsFromStorage = localStorage.getItem('presets')

        if (!presetsFromStorage) {
            return []
        }

        const presetArray:Preset[] = JSON.parse(presetsFromStorage)

        return presetArray
    }

    const removePreset = (id:Guid) => {
        let presetIndex = presets.findIndex(p => p.id === id);
        let updatedPresets = [...presets]
        if(presetIndex !== -1) {
            updatedPresets.splice(presetIndex,1)
            saveCurrentPresetState(updatedPresets)
        }
    }

    const saveCurrentPresetState = (data: Preset[]) => {
        setPresets(data)
        localStorage.setItem('presets', JSON.stringify(data))
    }

    const setPeriodFromPreset = (type: SelectedDatePeriodType) => {
        switch(type){
            case "Today" : {
                let dates = setTodayPeriod()
                return dates
            }
            case "Yesterday" : {
                let dates = setYesterdayPeriod()
                return dates
            }
            case "WeekToDate" : {
                let dates = setWeekToDatePeriod()
                return dates
            }
            case "LastWeek" : {
                let dates = setLastWeekPeriod()
                return dates
            }
            case "Last7Days" : {
                let dates = setLast7DaysPeriod()
                return dates
            }
            case "LastMonth" : {
                let dates = setLastMonthPeriod()
                return dates
            }
            case "MonthToDate" : {
                let dates = setMonthToDatePeriod()
                return dates
            }
            default: {
                return {start:"", end: ""}
            }
        }
    }

    const restoreDataFromPreset = async (id:Guid) => {
        let presetIndex = presets.findIndex(p => p.id === id);
        if(presetIndex !== -1) {
            let elem = presets[presetIndex]
            if(elem.periodType !== ""){
                setSelectedDatePeriodType(elem.periodType)
                let dates = setPeriodFromPreset(elem.periodType)
                elem.startDate = dates.start
                elem.endDate = dates.end
            } else {
                setSelectedDatePeriodType("")
                setSelectedStartDate(elem.startDate)
                setSelectedEndDate(elem.endDate)
            }
            setIsSelectedStartDateError(false)
            setIsSelectedEndDateError(false)
            if(elem.slices.findIndex(t => t.id === ReportDashboardColumns.ContentType) == -1){
                elem.slices.push({
                    id: 4,
                    name: ReportDashboardColumns[ReportDashboardColumns.ContentType],
                    isActive: false
                })
            }
            setSlices(elem.slices)
            setSelectedCampaigns(elem.campaignIds)
            setSelectedNetworks(elem.networkIds)
            setSelectedPlayers(elem.playerIds)
            setSelectedMedias(elem.mediaIds)
            setSelectedContentTypes(elem.contentTypes ?? [])



            await updateReportDashboardDataFromPreset(elem)
        } 

    }

    const renderPreset = (presets: Preset[]) => {
        return presets.map( el => {
            return <span 
                key={el.id} 
                className="presets__elem"
            >
                <span onClick={() => {restoreDataFromPreset(el.id)}}>{el.name} </span>
                <span className="close__element" onClick={()=>{removePreset(el.id)}}>x</span>
            </span>
        })
    }

    const downloadFile = async () => {
        let result = await downloadCSVReportsDashboardData({
            startTime: selectedStartDate,
            endTime: selectedEndDate,
            columns: getColumnsForRequest(slices),
            networkIds: selectedNetworks,
            campaignIds: selectedCampaigns,
            playerIds: selectedPlayers,
            mediaIds: selectedMedias,
            contentTypes: selectedContentTypes
        })
        fileDownload(result.data, result.filename);
    }

    return (
        <>

            <div className="presets mb-30">
                <div className="presets__block">
                    <h3>Use Preset</h3>
                    {renderPreset(presets)}
                </div>
                <div className="d-flex justify-content-between align-items-start gap15">
                    <Button color="green" label="Add Preset" onClick={() => {setIsShowPresetModal(true)}} isDisabled={isSelectedStartDateError || isSelectedEndDateError}/>
                    <Button color="green" label="Request AdHoc" onClick={() => {updateReportDashboardDataForAdhoc()}} isDisabled={isSelectedStartDateError || isSelectedEndDateError}/>
                    <Button color="green" label="To Adhoc Reports" onClick={() => {
                        props.history.push(Routes.ReportsAdhoc)
                    }}/>
                    <Button color="green" label="Scheduled Reports" onClick={()=>{setIsShowSheduledReportsModal(true)}}/>
                </div>
            </div>

            <div className="chart-block d-flex" style={{height:300}}>
                <ResponsiveContainer width="100%" height="100%">
                    {playoutsPerDay.length !== 0 ? <LineChart
                        width={500}
                        height={300}
                        data={playoutsPerDay}
                        margin={{
                            top: 0,
                            right: 0,
                            left: 0,
                            bottom: 0,
                        }}
                    >
                        <Legend align="left" verticalAlign="top" height={30} />
                        <CartesianGrid strokeDasharray="0 0" />
                        <XAxis dataKey="date" />
                        <YAxis yAxisId={"left"} dataKey="playouts" />
                        <YAxis yAxisId={"right"} orientation="right" dataKey="durationSeconds" />
                        <Tooltip />
                        <Line yAxisId={"left"} type="monotone" dataKey="playouts" name="Playouts" stroke="#8884d8" animationDuration={2000} activeDot={{ r: 6 }}/>
                        <Line yAxisId={"right"} type="monotone" dataKey="durationSeconds" name={`Duration ${durationMode}`} stroke="#ACE7A8" animationDuration={2000}/>
                    </LineChart> :          
                    <div style={{textAlign:"center", paddingTop:"150px", fontSize:"18px"}}>
                        Please use below options to build your report, click on Update button to display charts and stats.
                    </div>}             
                </ResponsiveContainer>
            </div>

            <h2 className="mb-5 pt-20">Report Period</h2>
            <div className="preset-date-inputs">
                <div className="d-flex align-items-center gap15" style={{width:"50%"}}>
                    <FormRow>
                        <FormInput
                            errorMessage="Please select start date that earlier then end date"
                            id="preset_start_date"
                            isError={isSelectedStartDateError}
                            type="date"
                            onChangeValue={(v) => {onStartDateChanged(v)}}
                            value={selectedStartDate}
                            title="Start"
                        />  
                    </FormRow>
                    <FormRow>
                        <FormInput
                            errorMessage=""
                            id="preset_end_date"
                            isError={isSelectedEndDateError}
                            type="date"
                            onChangeValue={(v) => {onEndDateChanged(v)}}
                            value={selectedEndDate}
                            title="End"
                        />   
                    </FormRow> 
                </div>
                <div className="dates-presets" style={{width:"50%"}}>
                    <span className={`${checkActiveDatePeriodType("Today")}`} onClick={setTodayPeriod}>Today</span>
                    <span className={`${checkActiveDatePeriodType("Yesterday")}`} onClick={setYesterdayPeriod}>Yesterday</span>
                    <span className={`${checkActiveDatePeriodType("WeekToDate")}`} onClick={setWeekToDatePeriod}>Week To Date</span>
                    <span className={`${checkActiveDatePeriodType("LastWeek")}`} onClick={setLastWeekPeriod}>Last Week</span>
                    <span className={`${checkActiveDatePeriodType("Last7Days")}`} onClick={setLast7DaysPeriod}>Last 7 Days</span>
                    <span className={`${checkActiveDatePeriodType("LastMonth")}`} onClick={setLastMonthPeriod}>Last Month</span>
                    <span className={`${checkActiveDatePeriodType("MonthToDate")}`} onClick={setMonthToDatePeriod}>Month To Date</span>
                </div>
            </div>

            <div className="slices">
                <h2>Splits</h2>
                <div className="d-flex justify-content-between">
                    <ReactSortable 
                        list={slices}
                        setList={(newState) => {
                            setSlices(newState)
                        }}
                        tag={"div"}
                        className="slices__sort-block"
                    >
                        {renderSlices(slices)}
                    </ReactSortable>
                    <div className="d-flex justify-content-end align-items-center gap15">
                        <Button color="green" label="Reset" onClick={() => setIsShowConfirmResetModal(true)} />
                        <Button color="green" label="Update" onClick={updateReportDashboardData} isDisabled={isSelectedStartDateError || isSelectedEndDateError}/>
                        {reportPlayouts.length ? <div className="export-pos">
                            <a className="link-like__elem md" target="_blank" onClick={downloadFile}>Download CSV <IconButton tooltipText="Download" type="export"  /> </a>
                        </div> : null}
                    </div>
                </div>
            </div>

            <div className="d-flex align-items-center pt-30 mb-20 w-100 gap15">
                <div className="w-100">
                    <label>Campaigns</label>
                    <Select 
                        options={filteredCampaigns}
                        classNamePrefix={`select`}
                        onChange={onChangeSelectedCampaign} 
                        value={filteredCampaigns.filter(c => selectedCampaigns.includes(c.value))}
                        isClearable={true}
                        isMulti={true}
                    />
                </div>
                <div className="w-100">
                    <label>Players</label>
                    <Select 
                        options={filteredPlayers}
                        classNamePrefix={`select`}
                        onChange={onChangeSelectedPlayer} 
                        value={filteredPlayers.filter(p => selectedPlayers.includes(p.value))}
                        isClearable={true}
                        isMulti={true}
                    />
                </div>
                <div className="w-100">
                    <label>Networks</label>
                    <Select 
                        options={filteredNetworks}
                        classNamePrefix={`select`}
                        onChange={onChangeSelectedNetwork} 
                        value={filteredNetworks.filter(n => selectedNetworks.includes(n.value))}
                        isClearable={true}
                        isMulti={true}
                    />
                </div>
                <div className="w-100">
                    <label>Media</label>
                    <Select 
                        options={filteredMedias}
                        classNamePrefix={`select`}
                        onChange={onChangeSelectedMedia} 
                        value={filteredMedias.filter(m => selectedMedias.includes(m.value))}
                        isClearable={true}
                        isMulti={true}
                    />
                </div>
                <div className="w-100">
                    <label>Content Types</label>
                    <Select 
                        options={filteredContentTypes}
                        classNamePrefix={`select`}
                        onChange={onChangeSelectedContentType} 
                        value={filteredContentTypes.filter(m => selectedContentTypes.includes(m.value))}
                        isClearable={true}
                        isMulti={true}
                    />
                </div>
            </div>

            <div className="d-flex justify-content-end pt-30 gap15">
                <span>Total Playouts: {totalPlayouts}</span>
                <span>Total Duration: {totalDurationString}</span>
            </div>

            {reportPlayouts.length ? <><table className="table">
                <thead>
                    <tr>
                        {renderTableTitles(sliceColumns)}
                        <td>Playouts</td>
                        <td>Duration</td>
                    </tr>
                </thead>
                <tbody>
                    {renderPlayoutRows(reportPlayoutsForDisplay, sliceColumns)}
                </tbody>
            </table>

            <Pagination activePage={currentPage} pagesCount={getPagesCount(reportPlayouts.length, itemsPerPage)} changePaginationPage={setCurrentPage} />
            </> : <p>No Data</p>}

            <RightModal title={`Add Preset`} show={isShowPresetModal} onRequestClose={hidePresetModal}>
                <FormRow> 
                    <FormInput 
                        errorMessage = {presetNameErrorMessage}
                        id = "preset_name"
                        isError = {isPresetNameError}
                        title = "Preset Name"
                        onChangeValue = {(v) => {setPresetName(v); setIsPresetNameError(false)}}
                        value = {presetName}
                    />
                </FormRow>
                <div className="modal-btn-group">
                    <Button color="green" label="Save" onClick={createNewPreset}/>
                    <Button color="gray" label="Cancel" onClick={hidePresetModal}/>
                </div>
            </RightModal> 

            <RightModal title={`Daily Report Schedule`} show={isShowSheduledReportsModal} onRequestClose={()=>{setIsShowSheduledReportsModal(false)}}>
                <ReportSubscriptions />
                <div className="modal-btn-group">
                    <div></div>
                    <Button color="gray" label="Close" onClick={()=>{setIsShowSheduledReportsModal(false)}}/>
                </div>
            </RightModal> 

            <SmallModal show={isShowConfirmResetModal} onRequestClose={() => {setIsShowConfirmResetModal(false)}}> 
                <p>Reset selected data</p>
                <div className="modal-btn-group">
                    <Button color="gray" label="Cancel" onClick={() => {setIsShowConfirmResetModal(false)}}/>
                    <Button color="green" label="Yes" onClick={resetReportDashboardData}/>
                </div>
            </SmallModal>

            <SmallModal show={isShowSmallModal} text="Your Request has been sent" onRequestClose={() => setIsShowSmallModal(false)}> 
                <Button color="green" label="Ok" onClick={() => setIsShowSmallModal(false)}/>
            </SmallModal>

            <GlobalSpinner isShow={isLoading} />
        </>
    )
}

export default withRouter(ReportDashboard)